import { useState } from 'react';
import type { Question } from '../types';

type QuestionDraft = {
  text: string;
  type: Question['type'];
  optionsText: string;
  isRequired: boolean;
};

function draftFromQuestion(q?: Question): QuestionDraft {
  if (!q) {
    return { text: '', type: 'text', optionsText: '', isRequired: true };
  }
  return {
    text: q.text,
    type: q.type,
    optionsText: q.options.join('\n'),
    isRequired: q.isRequired,
  };
}

function parseOptions(text: string): string[] {
  return text
    .split('\n')
    .map((s) => s.trim())
    .filter(Boolean);
}

interface QuestionEditorProps {
  initial?: Question;
  title: string;
  saving?: boolean;
  onSave: (data: {
    text: string;
    type: Question['type'];
    options: string[];
    isRequired: boolean;
  }) => void;
  onCancel: () => void;
}

function QuestionEditor({ initial, title, saving, onSave, onCancel }: QuestionEditorProps) {
  const [draft, setDraft] = useState<QuestionDraft>(() => draftFromQuestion(initial));

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!draft.text.trim()) return;

    const options = draft.type === 'single_choice' ? parseOptions(draft.optionsText) : [];
    if (draft.type === 'single_choice' && options.length === 0) return;

    onSave({
      text: draft.text.trim(),
      type: draft.type,
      options,
      isRequired: draft.isRequired,
    });
  }

  return (
    <form className="question-editor card" onSubmit={handleSubmit}>
      <h3>{title}</h3>

      <div className="field">
        <label>Текст вопроса</label>
        <textarea
          value={draft.text}
          onChange={(e) => setDraft((d) => ({ ...d, text: e.target.value }))}
          placeholder="Например: Откуда вы узнали о клинике?"
          rows={3}
        />
      </div>

      <div className="field">
        <label>Тип ответа</label>
        <select
          value={draft.type}
          onChange={(e) =>
            setDraft((d) => ({
              ...d,
              type: e.target.value as Question['type'],
            }))
          }
        >
          <option value="text">Свободный текст</option>
          <option value="phone">Телефон</option>
          <option value="single_choice">Выбор из вариантов</option>
        </select>
      </div>

      {draft.type === 'single_choice' && (
        <div className="field">
          <label>Варианты ответа (каждый с новой строки)</label>
          <textarea
            value={draft.optionsText}
            onChange={(e) => setDraft((d) => ({ ...d, optionsText: e.target.value }))}
            placeholder={'Социальные сети\nРекомендация знакомых\nДругое'}
            rows={5}
          />
        </div>
      )}

      <label className="checkbox-row">
        <input
          type="checkbox"
          checked={draft.isRequired}
          onChange={(e) => setDraft((d) => ({ ...d, isRequired: e.target.checked }))}
        />
        Обязательный вопрос
      </label>

      <div className="btn-row" style={{ marginTop: 12 }}>
        <button type="button" className="btn btn-secondary" onClick={onCancel} disabled={saving}>
          Отмена
        </button>
        <button type="submit" className="btn btn-primary" disabled={saving || !draft.text.trim()}>
          {saving ? 'Сохранение…' : 'Сохранить'}
        </button>
      </div>
    </form>
  );
}

interface QuestionsTabProps {
  questions: Question[];
  saving: boolean;
  onCreate: (data: {
    text: string;
    type: Question['type'];
    options: string[];
    isRequired: boolean;
  }) => Promise<void>;
  onUpdate: (
    id: number,
    data: {
      text: string;
      type: Question['type'];
      options: string[];
      isRequired: boolean;
    }
  ) => Promise<void>;
  onHide: (id: number) => Promise<void>;
  onRestore: (id: number) => Promise<void>;
}

export function QuestionsTab({
  questions,
  saving,
  onCreate,
  onUpdate,
  onHide,
  onRestore,
}: QuestionsTabProps) {
  const [editingId, setEditingId] = useState<number | null>(null);
  const [creating, setCreating] = useState(false);

  const activeQuestions = questions.filter((q) => q.isActive);
  const hiddenQuestions = questions.filter((q) => !q.isActive);

  return (
    <>
      <div className="card">
        <h2>Вопросы анкеты</h2>
        <p className="subtitle">Изменения применяются ко всем новым прохождениям</p>

        {!creating && (
          <button type="button" className="btn btn-primary" onClick={() => setCreating(true)}>
            Добавить вопрос
          </button>
        )}
      </div>

      {creating && (
        <QuestionEditor
          title="Новый вопрос"
          saving={saving}
          onCancel={() => setCreating(false)}
          onSave={async (data) => {
            await onCreate(data);
            setCreating(false);
          }}
        />
      )}

      {activeQuestions.map((q) => (
        <div key={q.id} className="question-admin card">
          {editingId === q.id ? (
            <QuestionEditor
              initial={q}
              title="Редактирование"
              saving={saving}
              onCancel={() => setEditingId(null)}
              onSave={async (data) => {
                await onUpdate(q.id, data);
                setEditingId(null);
              }}
            />
          ) : (
            <>
              <strong>{q.text}</strong>
              <div className="chip-list">
                <span className="chip">{q.type === 'single_choice' ? 'варианты' : q.type}</span>
                {q.isRequired && <span className="chip">обязательный</span>}
              </div>
              {q.options.length > 0 && (
                <div className="chip-list">
                  {q.options.map((o) => (
                    <span key={o} className="chip">
                      {o}
                    </span>
                  ))}
                </div>
              )}
              <div className="btn-row" style={{ marginTop: 8 }}>
                <button type="button" className="btn btn-secondary" onClick={() => setEditingId(q.id)}>
                  Редактировать
                </button>
                <button type="button" className="btn btn-danger" onClick={() => onHide(q.id)}>
                  Скрыть
                </button>
              </div>
            </>
          )}
        </div>
      ))}

      {hiddenQuestions.length > 0 && (
        <div className="card">
          <h3>Скрытые вопросы</h3>
          {hiddenQuestions.map((q) => (
            <div key={q.id} className="question-admin">
              <strong>{q.text}</strong>
              <button
                type="button"
                className="btn btn-secondary"
                style={{ marginTop: 8 }}
                onClick={() => onRestore(q.id)}
              >
                Восстановить
              </button>
            </div>
          ))}
        </div>
      )}
    </>
  );
}
